import { DataProvider } from '@refinedev/core';
import axiosInstance, { jsonHeaders } from '@/api/axiosInstance';
import { compactApiParams } from '@/api/queryParams';
import { logger } from '@/services/logging';
import { resolveRequestUserId } from '@/services/requestContext';

const DEFAULT_PAGE_SIZE = 25;

const buildOrderListPayload = ({
  page,
  perPage,
  sortParams,
  filterParams,
  meta,
}: {
  page: number;
  perPage: number;
  sortParams: Array<{ columnName: string; value: string }>;
  filterParams: Array<{ columnName: string; value: unknown }>;
  meta?: { extraPayload?: Record<string, unknown> };
}) => ({
  UserId: resolveRequestUserId(meta?.extraPayload?.userId as string | undefined),
  ProductType: null,
  Source: (meta?.extraPayload?.source as string | undefined) ?? '',
  RecordId: null,
  SearchFilters: (meta?.extraPayload?.searchFilters as string | null | undefined) ?? null,
  OrderStatus: (meta?.extraPayload?.orderStatus as string[] | undefined) ?? [],
  LinesPerPage: perPage,
  PageNumber: page,
  Filters: filterParams,
  Sorting: sortParams,
});

const NO_CACHE_HEADERS = {
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  Pragma: 'no-cache',
  Expires: '0',
};

const normalizeResourcePath = (resource: string) => resource.replace(/^\/+/, '').replace(/\/get$/i, '');

const isGetListResource = (resource: string) => ['simpleorder', 'Order'].includes(normalizeResourcePath(resource));

const buildSwaggerListParams = ({
  resource,
  page,
  perPage,
  sortParams,
  filterParams,
  meta,
}: {
  resource: string;
  page: number;
  perPage: number;
  sortParams: Array<{ columnName: string; value: string }>;
  filterParams: Array<{ columnName: string; value: unknown }>;
  meta?: { extraPayload?: Record<string, unknown> };
}) => {
  const normalizedResource = normalizeResourcePath(resource);

  if (normalizedResource === 'simpleorder') {
    return compactApiParams({
      SearchFilters: (meta?.extraPayload?.searchFilters as string | null | undefined) ?? null,
      OrderStatus: (meta?.extraPayload?.orderStatus as string[] | undefined) ?? [],
      ShipmentStatus: (meta?.extraPayload?.shipmentStatus as string[] | undefined) ?? [],
      ModeOfTransportation: (meta?.extraPayload?.modeOfTransportation as string[] | undefined) ?? [],
      Filters: filterParams,
      Sorting: sortParams,
      PageNumber: page,
      LinesPerPage: perPage,
    });
  }

  return compactApiParams({
    PageNumber: page,
    PageSize: perPage,
    Sorting: sortParams,
    Filters: filterParams,
    ...(meta?.extraPayload ?? {}),
  });
};

export const dataProvider: DataProvider = {
  getList: async ({ resource, pagination, sorters, filters, meta }) => {
    const page = pagination?.current ?? 1;
    const perPage = pagination?.pageSize ?? DEFAULT_PAGE_SIZE;

    const sortParams = (sorters ?? []).map((s) => ({
      columnName: s.field,
      value: s.order === 'asc' ? 'ASC' : 'DESC',
    }));

    const filterParams = (filters ?? []).map((f) => ({
      columnName: 'field' in f ? f.field : '',
      value: 'value' in f ? f.value : '',
    }));

    const payload = {
      pageNumber: page,
      linesPerPage: perPage,
      sorts: sortParams,
      filters: filterParams,
      ...(meta?.extraPayload ?? {}),
    };

    const requestPayload = resource === 'simpleorder/get' || resource === 'simpleorder' || resource === 'order/get'
      ? buildOrderListPayload({ page, perPage, sortParams, filterParams, meta: meta as { extraPayload?: Record<string, unknown> } | undefined })
      : payload;

    const requestOptions = (meta as { requestOptions?: { noCache?: boolean } } | undefined)?.requestOptions;
    const shouldBypassCache = !!requestOptions?.noCache;
    const normalizedResource = normalizeResourcePath(resource);
    const endpoint = shouldBypassCache ? `/${normalizedResource}?_=${Date.now()}` : `/${normalizedResource}`;
    const headers = shouldBypassCache
      ? { ...jsonHeaders(), ...NO_CACHE_HEADERS }
      : jsonHeaders();

    try {
      const isGetRequest = isGetListResource(resource);
      const response = isGetRequest
        ? await axiosInstance.get(endpoint, {
          params: buildSwaggerListParams({
            resource,
            page,
            perPage,
            sortParams,
            filterParams,
            meta: meta as { extraPayload?: Record<string, unknown> } | undefined,
          }),
          headers,
        })
        : await axiosInstance.post(
          endpoint,
          requestPayload,
          { headers },
        );

      const { data } = response;
      return {
        data: data.data ?? data.datas ?? data ?? [],
        total: data.count ?? data.totalCount ?? (data.data ?? data.datas ?? data).length,
      };
    } catch (error) {
      logger.error(`getList failed for ${resource}`, error as Error);
      throw error;
    }
  },

  getOne: async ({ resource, id }) => {
    try {
      const { data } = await axiosInstance.get(`/${resource}/${id}`, {
        headers: jsonHeaders(),
      });
      return { data };
    } catch (error) {
      logger.error(`getOne failed for ${resource}/${id}`, error as Error);
      throw error;
    }
  },

  create: async ({ resource, variables, meta }) => {
    try {
      const { data } = await axiosInstance.post(
        meta?.endpoint ?? `/${resource}`,
        variables,
        { headers: jsonHeaders() },
      );
      return { data };
    } catch (error) {
      logger.error(`create failed for ${resource}`, error as Error);
      throw error;
    }
  },

  update: async ({ resource, id, variables, meta }) => {
    const url = meta?.endpoint ?? `/${resource}/${id}`;
    try {
      const { data } = await axiosInstance.put(url, variables, {
        headers: jsonHeaders(),
      });
      return { data };
    } catch (error) {
      logger.error(`update failed for ${resource}/${id}`, error as Error);
      throw error;
    }
  },

  deleteOne: async ({ resource, id }) => {
    try {
      const { data } = await axiosInstance.delete(`/${resource}/${id}`, {
        headers: jsonHeaders(),
      });
      return { data };
    } catch (error) {
      logger.error(`deleteOne failed for ${resource}/${id}`, error as Error);
      throw error;
    }
  },

  getApiUrl: () => import.meta.env.VITE_API_ENDPOINT ?? '',
};
